/**
 * Leads API Types
 *
 * Aligned with docs/ACUMATICA-INTEGRATION-2.md and OpenAPI specification.
 */

// ─── Enums ────────────────────────────────────────────────────────────

export enum LeadStatus {
    NEW = 'new',
    CONTACTED = 'contacted',
    ENGAGED = 'engaged',
    QUALIFIED = 'qualified',
    UNQUALIFIED = 'unqualified',
    CONVERTED = 'converted',
}

export enum LeadPriority {
    LOW = 'low',
    MEDIUM = 'medium',
    HIGH = 'high',
    CRITICAL = 'critical',
}

export enum LeadSource {
    MANUAL = 'manual',
    STORM_TRACKER = 'storm_tracker',
    WEB_FORM = 'web_form',
    CAMPAIGN = 'campaign',
    REFERRAL = 'referral',
    ENRICHMENT = 'enrichment',
}

export enum LeadSyncStatus {
    PENDING = 'pending',
    SYNCED = 'synced',
    ERROR = 'error',
}

// ─── Models ───────────────────────────────────────────────────────────

export interface LeadExternalIdentifier {
    id: string
    system: string
    externalId: string
    externalUrl?: string
    syncStatus: LeadSyncStatus
    lastSyncedAt?: string
    metadata?: Record<string, unknown>
}

export interface LeadTouchpoint {
    id: string
    eventType: string
    source: LeadSource
    summary?: string
    occurredAt: string
    metadata?: Record<string, unknown>
}

export interface LeadLink {
    id: string
    targetType: 'property' | 'contact' | 'tenant' | 'project' | 'weather_event' | 'campaign_url' | 'custom_url'
    targetId?: string
    label?: string
    url?: string
    metadata?: Record<string, unknown>
}

export interface Lead {
    id: string
    organizationId: string
    title: string
    description?: string
    status: LeadStatus
    priority: LeadPriority
    source: LeadSource
    score: number
    estimatedValue?: number
    currency: string
    companyName?: string
    primaryContactName?: string
    primaryContactEmail?: string
    primaryContactPhone?: string
    propertyId?: string
    contactId?: string
    touchpointCount: number
    lastTouchpointAt: string
    convertedOpportunityId?: string
    convertedAt?: string
    disqualificationReason?: string
    customFields?: Record<string, unknown>
    metadata?: Record<string, unknown>
    touchpoints?: LeadTouchpoint[]
    links?: LeadLink[]
    externalIdentifiers?: LeadExternalIdentifier[]
    createdAt: string
    updatedAt: string
}

// ─── DTOs & Request/Response Types ─────────────────────────────────────

export interface LeadFilters {
    page?: number
    limit?: number
    status?: string
    priority?: string
    source?: string
    minScore?: number
    maxScore?: number
    search?: string
    propertyId?: string
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
}

export interface PaginatedLeadsResponse {
    ok: boolean
    data: {
        items: Lead[]
        total: number
        page: number
        limit: number
        totalPages: number
    }
}

export interface CreateLeadDto {
    title: string
    description?: string
    companyName?: string
    primaryContactName?: string
    primaryContactEmail?: string
    primaryContactPhone?: string
    propertyId?: string
    contactId?: string
    source?: LeadSource
    priority?: LeadPriority
    score?: number
    estimatedValue?: number
    currency?: string
    customFields?: Record<string, unknown>
    metadata?: Record<string, unknown>
}

export interface UpdateLeadDto {
    title?: string
    description?: string
    companyName?: string
    primaryContactName?: string
    primaryContactEmail?: string
    primaryContactPhone?: string
    propertyId?: string
    contactId?: string
    source?: LeadSource
    priority?: LeadPriority
    score?: number
    estimatedValue?: number
    currency?: string
    customFields?: Record<string, unknown>
    metadata?: Record<string, unknown>
}

export interface LeadStatusTransitionDto {
    status: LeadStatus
    disqualificationReason?: string
}

export interface LeadConvertDto {
    convertedOpportunityId?: string
    estimatedValue?: number
}

export interface LeadLinkDto {
    targetType: LeadLink['targetType']
    targetId?: string
    label?: string
    url?: string
    metadata?: Record<string, unknown>
}

export interface LeadExternalIdDto {
    system: string
    externalId: string
    externalUrl?: string
    syncStatus?: LeadSyncStatus
    metadata?: Record<string, unknown>
}

export interface LeadIngestDto {
    name: string
    email: string
    phone?: string
    company?: string
    title?: string
    description?: string
    address?: string
    city?: string
    state?: string
    postalCode?: string
    utmSource?: string
    utmMedium?: string
    utmCampaign?: string
    _gotcha?: string
}
